// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { LinkPresentationProps } from "@insite/mobius/Link";
import { TypographyPresentationProps } from "@insite/mobius/Typography/Typography";
import { searchInputStyles } from "@sc/Overrides/Widgets/Header/SearchInput";
import ArrowRight from "@sc/XDL/Icons/ArrowRight";
import Search from "@sc/XDL/Icons/Search";
import { color, createLinkIconStyles, mergeWithCss, styledScrollbar, xScreen } from "@sc/XDL/Utilities";
import { css } from "styled-components";

interface AutoCompleteItems {
    headerText?: TypographyPresentationProps;
    itemText?: TypographyPresentationProps;
    link?: LinkPresentationProps;
    focusedItemText?: TypographyPresentationProps;
    focusedLink?: LinkPresentationProps;
}

const sharedAutoCompleteTextStyles: TypographyPresentationProps = {
    size: 12,
    weight: "normal",
    color: "text.main",
    css: css`
        letter-spacing: 0;
        line-height: 13px;
    `,
};

export const autoCompleteSharedStyles: AutoCompleteItems = {
    headerText: {
        size: 14,
        weight: "bold",
        color: "text",
        css: css`
            display: block;
            letter-spacing: 0;
            line-height: 19px;
            margin: 0 0 8px 0;
        `,
    },
    itemText: {
        ...mergeWithCss(sharedAutoCompleteTextStyles, {
            css: css`
                margin-bottom: 13px;
            `,
        }),
    },
    link: {
        mergeCss: true,
        color: "text.main",
        typographyProps: {
            ...sharedAutoCompleteTextStyles,
            css: css`
                letter-spacing: 0;
                line-height: 13px;
                b {
                    color: ${color("text.link")};
                }
            `,
        },
        css: css`
            &:hover,
            &:focus {
                color: ${color("common.ui8")};
                b {
                    color: ${color("common.ui8")};
                }
            }
        `,
    },
    focusedItemText: {
        ...mergeWithCss(sharedAutoCompleteTextStyles, {
            css: css`
                margin-bottom: 8px;
            `,
        }),
    },
    focusedLink: {
        typographyProps: {
            ...sharedAutoCompleteTextStyles,
        },
        css: css`
            background: ${color("common.ui3")};
        `,
    },
};

searchInputStyles.popover = {
    transitionDuration: "regular",
    toggle: false,
};

searchInputStyles.popoverWrapper = {
    as: "div",
    _height: "auto",
    css: css`
        display: inline-flex;
        justify-content: flex-end;
    `,
};

searchInputStyles.popoverContentBody = {
    as: "div",
    _height: "100%",
};
searchInputStyles.autocompleteContainer = {
    css: css`
        overflow: auto;
        ${styledScrollbar(4)}
        max-height: calc(100vh - 102px); // header height
    `,
};
searchInputStyles.autocompleteWrapper = {
    css: css`
        display: flex;
        flex-direction: row;
        ${({ hasContent, width }: any) => (hasContent ? `min-width: ${width}px;` : `width: ${width || 652}px;`)}
        ${xScreen("lte", "md")} {
            flex-direction: column;
            width: 100%;
        }
    `,
};

searchInputStyles.autocompleteColumnWrapper = {
    css: css`
        display: flex;
        flex-direction: column;
        gap: 5px;
        width: 228px;
        padding: 13px;
        border-right: 1px solid ${color("common.border")};
        background-color: ${color("common.white")};
        border-bottom: none;
        border-top: none;
        border-left: none;
        ${xScreen("lte", "md")} {
            width: 100%;
            border-right: none;
            padding: 16px 16px 11px;
        }
    `,
};

searchInputStyles.autocompleteColumnProductWrapper = {
    css: css`
        ${({ hasContent }: any) => (hasContent ? "width: 658px" : "width: 100%")};
        padding: 13px;
        ${xScreen("lte", "md")} {
            padding: 16px;
            width: auto;
        }
        ${xScreen("lte", "sm")} {
            width: auto;
            padding-top: 0;
        }
    `,
};
searchInputStyles.autocompleteColumnProductContentWrapper = {
    css: css`
        display: flex;
        flex-wrap: wrap;
        gap: 16px 22px;
    `,
};

searchInputStyles.tippyPopup = {
    tooltipContent: {
        css: css`
            border: 1px solid ${color("common.border")};
            ${xScreen("lte", "md")} {
                width: 100%;
                border: none;
                border-radius: unset;
                top: 10px !important;
            }
            ${xScreen("lte", "xs")} {
                top: 11px !important;
            }
        `,
    },
};

searchInputStyles.autocompleteCategoriesStyles = {};
searchInputStyles.autocompleteBrandsStyles = {
    headerText: {
        ...autoCompleteSharedStyles?.headerText,
    },
    link: {
        ...autoCompleteSharedStyles?.link,
    },
    focusedBrandItemText: {
        ...autoCompleteSharedStyles?.focusedItemText,
    },
    focusedLink: {
        ...autoCompleteSharedStyles?.focusedLink,
    },
    brandItemText: {
        ...autoCompleteSharedStyles?.itemText,
    },
};
searchInputStyles.autocompleteContentStyles = {
    headerText: {
        ...autoCompleteSharedStyles?.headerText,
    },
    link: {
        ...autoCompleteSharedStyles?.link,
    },
    focusedLink: {
        ...autoCompleteSharedStyles?.focusedLink,
    },
};
searchInputStyles.autocompleteColumnViewAllProductWrapper = {
    css: css`
        display: flex;
        justify-content: space-between;
        margin-bottom: 8px;
        ${xScreen("lte", "md")} {
            margin-bottom: 16px;
            padding-top: 24px;
            border-top: 1px solid ${color("common.border")};
        }
    `,
};

searchInputStyles.autocompleteColumnViewAllProductText = {
    weight: "bold",
};

searchInputStyles.input = {
    sizeVariant: "default",
    iconProps: { src: Search },
    cssOverrides: {
        inputSelect: css`
            &[aria-expanded="true"] {
                border-radius: 4px 4px 0 0;
            }
            ${xScreen("lte", "md")} {
                &[aria-expanded="true"] {
                    border-radius: 4px;
                }
            }
        `,

        formField: css`
            width: 100%;
        `,
    },
};
searchInputStyles.viewAllLink = {
    ...createLinkIconStyles(ArrowRight, {
        typographyProps: {
            size: 12,
        },
        icon: {
            position: "right",
            iconProps: {
                size: 10,
            },
        },
    }),
};
